import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { saveAs } from "file-saver";
import { Form, Spin, Dropdown, Upload, Button, Input } from "antd";
import { LoadingOutlined, UploadOutlined } from "@ant-design/icons";
import SkeletonTable from "../../components/table/SkeletonTable";
import columns from "../../columns/files/reviewFile";
import {
  CompleteTaskApi,
  ExportFileApi,
  ExportPdfFileApi,
  GetFileAnswerApi,
  GetFileDetailsApi,
  ReviewAnswerApi,
  UnsubmitFileApi,
} from "../../api/request/service";
import ConfirmationModal from "../../components/modals/ConfirmationModal";
import ModalComponent from "../../components/modals/ModalComponent";
import LoadableButton from "../../components/button/LoadableButton";
import Edit from "../../assets/Edit";
import AnswerModal from "./components/AnswerModal";
import ViewFilesModal from "./components/ViewFilesModal";
import { ApprovePaymentApi } from "../../api/request/report";
import BackButton from "../../components/button/BackButton";
import UnSubmitted from "../../assets/UnSubmitted";
import Add from "../../assets/Add";
import Export from "../../assets/Export";
import SendSignature from "./components/SendSignature";
import View from "../../assets/View";
import ViewSignatureModal from "./components/ViewSignatureModal";

const ReviewFile = () => {
  const { fileTypeId, id } = useParams();
  const navigate = useNavigate();
  const { state } = useLocation();
  const [fileData, setFileData] = useState(null);
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isUnsubmitOpen, setIsUnsubmitOpen] = useState(false);
  const [isUnsubmitLoading, setIsUnsubmitLoading] = useState(false);
  const [isCompleteTaskLoading, setIsCompleteTaskLoading] = useState(false);

  const [type, setType] = useState("review");

  const [isForceCompleteTask, setIsForceCompleteTask] = useState(false);
  const [isForceCompleteLoading, setIsForceCompleteLoading] = useState(false);

  const [isAnswerOpen, setIsAnswerOpen] = useState(false);
  const [isFilesOpen, setIsFilesOpen] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState({});

  const [isExportFileLoading, setIsExportFileLoading] = useState(false);

  const [paymentData, setPaymentData] = useState(null);
  const [isApproveLoading, setIsApproveLoading] = useState(false);

  const [isSignatureOpen, setIsSignatureOpen] = useState(false);

  const [acknowledgeForm] = Form.useForm();
  const [isAcknowledgeOpen, setIsAcknowledgeOpen] = useState(false);
  const [isAcknowledge, setIsAcknowledge] = useState(false);
  const [isFileError, setIsFileError] = useState(false);

  const getAnswer = async () => {
    try {
      setIsLoading(true);
      let params = { file_id: id };
      const { data } = await GetFileAnswerApi(params);
      if (data?.status) {
        setData(
          data?.data?.map((section) => {
            return {
              ...section,
              questions: section?.questions?.map((question) => {
                return {
                  ...question,
                  response_text: (
                    <div className="flex items-center gap-3 max-w-[250px]">
                      {question?.response_text && (
                        <p className="text-wrap">{question?.response_text}</p>
                      )}
                      {question?.field_type !== "file" && (
                        <div
                          className="cursor-pointer"
                          onClick={() => {
                            setSelectedQuestion(question);
                            setIsAnswerOpen(true);
                          }}
                        >
                          <Edit />
                        </div>
                      )}
                    </div>
                  ),
                  ...(question?.documents?.length && {
                    response_text: (
                      <a
                        className="text-blue-500"
                        onClick={() => {
                          setSelectedQuestion(question);
                          setIsFilesOpen(true);
                        }}
                      >
                        View Files
                      </a>
                    ),
                  }),
                  question: (
                    <div
                      className={`${
                        !question?.reviewed_checked
                          ? "sub-title highlight-text"
                          : ""
                      }`}
                    >
                      {question?.field_label || "--"}
                    </div>
                  ),
                  review: (
                    <div
                      className="cursor-pointer"
                      onClick={() =>
                        reviewHandler(
                          section?.id,
                          question?.id,
                          question?.reviewed_checked
                        )
                      }
                    >
                      <img
                        className="size-7"
                        src={
                          question?.reviewed_checked
                            ? "/assets/icons/outlined-check-circle.svg"
                            : "/assets/icons/outlined-circle.svg"
                        }
                      />
                    </div>
                  ),
                };
              }),
            };
          })
        );
      }
      setIsLoading(false);
    } catch (error) {
      if (error?.response?.data?.message)
        toast.error(error?.response?.data?.message);
      setIsLoading(false);
    }
  };

  const getFileDetails = async () => {
    try {
      setIsLoading(true);
      let params = { file_id: id };
      const { data } = await GetFileDetailsApi(params);
      if (data?.status) {
        setFileData(data?.data);
        if (data?.data?.payment_history?.length)
          setPaymentData(data?.data?.payment_history[0]);
      }
      setIsLoading(false);
    } catch (error) {
      if (error?.response?.data?.message)
        toast.error(error?.response?.data?.message);
      setIsLoading(false);
    }
  };

  const beforeUpload = (file) => {
    const isPDF = file.type === "application/pdf";
    if (!isPDF) {
      setIsFileError(true);
      return true;
    } else {
      setIsFileError(false);
      return false;
    }
  };

  useEffect(() => {
    if (id) {
      getFileDetails();
      getAnswer();
    }
  }, [id]);

  const spinner = (
    <div className="my-[3px]">
      <Spin indicator={<LoadingOutlined />} />
    </div>
  );

  const setReviewData = (sectionId, questionId, status, isLoading) => {
    setData((prev) => {
      return prev.map((section) => {
        if (section.id === sectionId) {
          return {
            ...section,
            questions: section?.questions?.map((question) => {
              if (question.id === questionId) {
                return {
                  ...question,
                  reviewed_checked: status,
                  question: (
                    <div
                      className={`${!status ? "sub-title highlight-text" : ""}`}
                    >
                      {question?.field_label || "--"}
                    </div>
                  ),
                  review: isLoading ? (
                    spinner
                  ) : (
                    <div
                      className="cursor-pointer"
                      onClick={() =>
                        reviewHandler(sectionId, questionId, status)
                      }
                    >
                      <img
                        className="size-7"
                        src={
                          status
                            ? "/assets/icons/outlined-check-circle.svg"
                            : "/assets/icons/outlined-circle.svg"
                        }
                      />
                    </div>
                  ),
                };
              }
              return question;
            }),
          };
        }
        return section;
      });
    });
  };

  const reviewHandler = async (sectionId, questionId, status) => {
    try {
      setReviewData(sectionId, questionId, !status, true);

      let params = {
        id: questionId,
        reviewed_checked: !status,
      };
      const { data } = await ReviewAnswerApi(params);
      if (data?.status) setReviewData(sectionId, questionId, !status, false);
    } catch (error) {
      if (error?.response?.data?.message)
        toast.error(error?.response?.data?.message);
      setReviewData(sectionId, questionId, status, false);
    }
  };

  const extractOriginFileObjs = (fileArray) => {
    return fileArray.map((file) => file.originFileObj);
  };

  const completeTask = async (values) => {
    try {
      setIsCompleteTaskLoading(true);

      const params = {
        file_id: id,
        ...(isAcknowledge && {
          acknowledge_ment_no: values?.acknowledgeNo,
          acknowledge_document: extractOriginFileObjs(
            values?.acknowledgeDoc?.fileList
          ),
        }),
      };

      const apiHeader = {
        headers: {
          Accept: "application/json",
          "Content-Type": "multipart/form-data",
        },
      };

      const { data } = await CompleteTaskApi(params, apiHeader);
      if (data?.status) {
        if (data?.data?.payment === "unpaid") {
          setIsForceCompleteTask(true);
        } else {
          toast.success(data?.message);
          navigate(`/file/${fileTypeId}`, {
            state: { task: state?.task?.name },
          });
          acknowledgeForm.resetFields();
        }
      }
      setIsCompleteTaskLoading(false);
    } catch (error) {
      if (error?.response?.data?.message)
        toast.error(error?.response?.data?.message);
      setIsCompleteTaskLoading(false);
    }
  };

  const forceCompleteTask = async () => {
    try {
      setIsForceCompleteLoading(true);
      const { data } = await CompleteTaskApi({
        file_id: id,
        is_force_update: true,
      });
      if (data?.status) {
        toast.success(data?.message);
        setIsForceCompleteTask(false);
        navigate(`/file/${fileTypeId}`, { state: { task: state?.task?.name } });
      }
      setIsForceCompleteLoading(false);
    } catch (error) {
      if (error?.response?.data?.message)
        toast.error(error?.response?.data?.message);
      setIsForceCompleteLoading(false);
    }
  };

  const unsubmitFile = async () => {
    if (fileData?.unsubmit === 1) return;
    try {
      setIsUnsubmitLoading(true);
      const { data } = await UnsubmitFileApi({ file_id: id });
      if (data?.status) {
        toast.success(data?.message);
        setIsUnsubmitOpen(false);
        setFileData({ ...fileData, unsubmit: 1 });
      }
      setIsUnsubmitLoading(false);
    } catch (error) {
      if (error?.response?.data?.message)
        toast.error(error?.response?.data?.message);
      setIsUnsubmitLoading(false);
    }
  };

  const exportFile = async (type) => {
    try {
      setIsExportFileLoading(true);
      const headers = { responseType: "arraybuffer" };
      let bufferData = {};
      if (type === "excel") {
        const { data } = await ExportFileApi({ file_id: id }, headers);
        bufferData = data;
      } else {
        const { data } = await ExportPdfFileApi({ file_id: id }, headers);
        bufferData = data;
      }
      const blob = new Blob([bufferData]);
      const fileName = `${fileData?.service_name_slug}.${
        type === "excel" ? "xlsx" : "pdf"
      }`;
      saveAs(blob, fileName);
    } catch (error) {
      if (error?.response?.data?.message)
        toast.error(error?.response?.data?.message);
    } finally {
      setIsExportFileLoading(false);
    }
  };

  const approvePayment = async () => {
    try {
      setIsApproveLoading(true);
      const { data } = await ApprovePaymentApi({ file_id: id });
      if (data?.status) setPaymentData({ ...paymentData, status: "APPROVED" });
    } catch (error) {
      if (error?.response?.data?.message)
        toast.error(error?.response?.data?.message);
    } finally {
      setIsApproveLoading(false);
    }
  };

  const items = [
    {
      key: "excel",
      label: <span className="sub-title">As Excel</span>,
      onClick: () => exportFile("excel"),
    },
    {
      key: "pdf",
      label: <span className="sub-title">As PDF</span>,
      onClick: () => exportFile("pdf"),
    },
  ];

  return (
    <>
      <div className="bg-white border rounded-xl p-5">
        <div className="flex md:flex-row flex-col justify-between gap-5 mb-5">
          <div className="flex items-center gap-3">
            <BackButton />
            <h2 className="title">{state?.task?.name || "Review File"}</h2>
          </div>
          {type === "review"
            ? fileData && (
                <div>
                  <div className="flex items-center gap-3 text-nowrap overflow-x-auto">
                    {fileData?.invoice_pdf?.url ? (
                      <LoadableButton
                        className="!px-3 !text-sm w-[120px]"
                        onClick={() =>
                          window.open(
                            fileData?.invoice_pdf?.url,
                            "_blank",
                            "noopener noreferrer"
                          )
                        }
                      >
                        <div className="flex gap-2">
                          <View color={"#fff"} /> Invoice
                        </div>
                      </LoadableButton>
                    ) : (
                      <LoadableButton
                        className="!px-3 !text-sm w-[120px]"
                        lable={"Send Invoice"}
                        onClick={() =>
                          navigate(`/file/${id}/send_invoice`, {
                            state: { fileData, fileTypeId },
                          })
                        }
                      >
                        <Add />
                        Invoice
                      </LoadableButton>
                    )}

                    {paymentData?.payment_type === "interac" &&
                      paymentData?.status === "PENDING" && (
                        <LoadableButton
                          className="!px-3 !text-sm w-[120px]"
                          lable={
                            paymentData?.status === "PENDING"
                              ? "Approve"
                              : "Approved"
                          }
                          onClick={approvePayment}
                          isLoading={isApproveLoading}
                          loadingLable="Approving..."
                          disabled={paymentData?.status === "APPROVED"}
                        />
                      )}

                    {state?.task?.is_signature ? (
                      <LoadableButton
                        className="!px-3 !text-sm w-[120px]"
                        lable={"Send to Sign"}
                        onClick={() => setType("signature")}
                      >
                        <Add />
                        Signature{" "}
                      </LoadableButton>
                    ) : null}

                    <LoadableButton
                      className="!px-3 !text-sm w-[120px]"
                      onClick={() => setIsUnsubmitOpen(true)}
                      disabled={fileData?.unsubmit === 1}
                    >
                      <UnSubmitted />{" "}
                      {fileData?.unsubmit === 1 ? "Unsubmitted" : "Unsubmit"}
                    </LoadableButton>

                    <Dropdown menu={{ items }} trigger={["click"]}>
                      <LoadableButton
                        className="!px-3 !text-sm w-[120px]"
                        lable={"Export File"}
                        isLoading={isExportFileLoading}
                        loadingLable="Exporting File..."
                      >
                        <Export /> Export
                      </LoadableButton>
                    </Dropdown>
                  </div>
                  {fileData?.is_signature_send ? (
                    <div className="text-right pt-3">
                      <p
                        onClick={() => setIsSignatureOpen(true)}
                        className="text-primary cursor-pointer"
                      >
                        View Signature{" "}
                      </p>
                    </div>
                  ) : null}
                </div>
              )
            : null}
        </div>

        {isLoading ? (
          <div className="flex justify-center flex-col mt-[20%]">
            <Spin size="large" />
            <p className="primary text-center mt-2">Loading...</p>
          </div>
        ) : (
          <>
            {type === "review" && data?.length ? (
              <>
                {data?.map((item, index) => (
                  <div className="table-radius-0 text-light mb-10">
                    <p className="title text-primary mb-5">
                      {`Section ${index + 1}:`} {item?.section_name}
                    </p>
                    <SkeletonTable
                      columns={columns}
                      data={item?.questions}
                      isLoading={isLoading}
                    />
                  </div>
                ))}

                <LoadableButton
                  type="submit"
                  lable="Complete Task"
                  onClick={() =>
                    state?.isLastTask
                      ? setIsAcknowledgeOpen(true)
                      : completeTask()
                  }
                  isLoading={isCompleteTaskLoading}
                  loadingLable="Completing Task..."
                />
              </>
            ) : null}

            {type === "signature" ? (
              <SendSignature
                setType={setType}
                file={{
                  id,
                  taskId: state?.task?.id,
                  clientId: state?.client?.id,
                }}
              />
            ) : null}
          </>
        )}
      </div>

      {/* Unsubmit Organizer */}
      <ConfirmationModal
        isOpen={isUnsubmitOpen}
        setIsOpen={setIsUnsubmitOpen}
        message="Are you sure, want to unsubmit this File?"
        onConfirm={unsubmitFile}
        isLoading={isUnsubmitLoading}
        loadingLabel="Unsubmiting..."
      />

      {/* final close file */}
      <ModalComponent
        isOpen={isAcknowledgeOpen}
        setIsOpen={setIsAcknowledgeOpen}
        title={`${
          !isAcknowledge
            ? "Do you want to attach a final acknowledgement to this file?"
            : "Add acknowledgement details"
        }`}
      >
        {!isAcknowledge ? (
          <div className="flex justify-center items-center md:gap-5 gap-3 mt-4">
            <LoadableButton
              lable="No"
              className="!bg-[#D91819] hover:!bg-[#aa0001]"
              onClick={() => completeTask()}
            />
            <LoadableButton
              lable="Yes"
              className="!bg-[#2D9B63] hover:!bg-[#096034]"
              onClick={() => setIsAcknowledge(true)}
            />
          </div>
        ) : (
          <div className="flex flex-col gap-4 mt-4 items-center w-full">
            <Form
              form={acknowledgeForm}
              layout="vertical"
              className="w-full"
              onFinish={completeTask}
            >
              <Form.Item label="Acknowledgement Number" name="acknowledgeNo">
                <Input placeholder="Enter a number" className="w-full" />
              </Form.Item>

              <Form.Item
                label="Acknowledgement Document"
                name="acknowledgeDoc"
                rules={[
                  {
                    required: true,
                    message: "Please upload at least one file",
                  },
                ]}
              >
                <Upload
                  multiple={true}
                  accept="application/pdf"
                  beforeUpload={beforeUpload}
                >
                  <Button icon={<UploadOutlined />}>Select Files</Button>
                </Upload>
                {isFileError ? (
                  <p className="text-red-500">
                    Only PDF file is allowed. Please upload a valid PDF
                    document.
                  </p>
                ) : null}
              </Form.Item>

              <LoadableButton
                type="submit"
                lable="Submit"
                isLoading={isCompleteTaskLoading}
                loadingLable="Submitting..."
              />
            </Form>
          </div>
        )}
      </ModalComponent>

      {/* Forcefully complete task */}
      <ConfirmationModal
        isOpen={isForceCompleteTask}
        setIsOpen={setIsForceCompleteTask}
        message={`Are you sure you want to complete this task without payment?`}
        onConfirm={forceCompleteTask}
        type="success"
        isLoading={isForceCompleteLoading}
        loadingLabel={`Completing task...`}
      />

      {/* Edit Answer */}
      <AnswerModal
        isOpen={isAnswerOpen}
        setIsOpen={setIsAnswerOpen}
        question={selectedQuestion}
        reloadQuestions={getAnswer}
      />

      {/* View Files */}
      <ViewFilesModal
        isOpen={isFilesOpen}
        setIsOpen={setIsFilesOpen}
        question={selectedQuestion}
      />

      {/* View Signature */}
      <ViewSignatureModal
        isOpen={isSignatureOpen}
        setIsOpen={setIsSignatureOpen}
        clientId={fileData?.user_id}
      />
    </>
  );
};

export default ReviewFile;
